import { useEffect, useState } from 'react';
import { COST_CATEGORY_LABELS } from '@camper-diary/shared';
import { getTripStats, type TripStats } from '../api/stats';

export function StatsSection({ tripId }: { tripId: string }) {
  const [stats, setStats] = useState<TripStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      setStats(await getTripStats(tripId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento statistiche');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  return (
    <section className="stats-section">
      <div className="section-head">
        <h2>Statistiche</h2>
        <button className="btn btn-ghost" onClick={() => void refresh()} disabled={loading}>
          🔄 {loading ? 'Aggiorno…' : 'Aggiorna'}
        </button>
      </div>

      {error && <p className="err">{error}</p>}
      {!stats && !error && <p className="muted">Calcolo…</p>}

      {stats && (
        <>
          {/* Riepilogo numerico */}
          <div className="stats-summary">
            <StatCard label="Tappe" value={stats.counts.stages.toString()} />
            <StatCard label="Post diario" value={stats.counts.posts.toString()} />
            <StatCard label="Foto" value={stats.counts.photos.toString()} />
            <StatCard label="Spese" value={stats.counts.costs.toString()} />
            <StatCard
              label="Durata"
              value={stats.duration.days !== null ? `${stats.duration.days} giorni` : '—'}
              hint={
                stats.duration.startDate && stats.duration.endDate
                  ? `${formatIt(stats.duration.startDate)} → ${formatIt(stats.duration.endDate)}`
                  : 'imposta date inizio/fine viaggio'
              }
            />
            <StatCard
              label="Km stradali stimati"
              value={
                stats.route
                  ? `~${Math.round(stats.route.distanceKm)} km`
                  : stats.counts.stages < 2
                    ? '—'
                    : 'n/d'
              }
              hint={
                stats.route
                  ? `~${formatHours(stats.route.durationHours)} di guida ottimale`
                  : stats.counts.stages < 2
                    ? 'servono almeno 2 tappe'
                    : 'OSRM non disponibile'
              }
            />
          </div>

          {/* Spese per valuta + categoria */}
          {stats.costs.byCurrency.length > 0 && (
            <div className="stats-block">
              <h3>Spese</h3>
              {stats.costs.byCurrency.map((bucket) => (
                <div key={bucket.currency} className="cost-breakdown">
                  <div className="cb-head">
                    <strong>{bucket.currency}</strong>
                    <span className="cb-total">{formatMoney(bucket.total, bucket.currency)}</span>
                  </div>
                  <ul className="cb-bars">
                    {bucket.byCategory.map((c) => {
                      const meta = COST_CATEGORY_LABELS[c.category];
                      const pct = bucket.total > 0 ? (c.amount / bucket.total) * 100 : 0;
                      return (
                        <li key={c.category}>
                          <div className="cb-row">
                            <span className="cb-label">
                              {meta.icon} {meta.label}
                            </span>
                            <span className="cb-value">
                              {formatMoney(c.amount, bucket.currency)}{' '}
                              <span className="muted">· {pct.toFixed(0)}%</span>
                            </span>
                          </div>
                          <div className="cb-bar">
                            <div className="cb-fill" style={{ width: `${pct}%` }} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Luoghi visitati */}
          {stats.places.length > 0 && (
            <div className="stats-block">
              <h3>Luoghi visitati ({stats.places.length})</h3>
              <ol className="places-list">
                {stats.places.map((p) => (
                  <li key={p.stageId}>
                    <strong>{p.title}</strong>
                    {p.address && <span className="muted"> · {p.address}</span>}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {hint && <div className="stat-hint muted">{hint}</div>}
    </div>
  );
}

function formatIt(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function formatHours(h: number): string {
  if (h < 1) return `${Math.round(h * 60)} min`;
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return mm > 0 ? `${hh}h ${mm}m` : `${hh}h`;
}

function formatMoney(n: number, cur: string): string {
  try {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: cur,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${cur}`;
  }
}
